import { type FC, useEffect, useState } from 'react';
import { Table } from '../Table';
import { Card } from '../Card';
import { useLeaderboardData } from './hooks/useLeaderboardData';
import { LabelledToggle } from '../Toggle';
import styled, { useTheme } from 'styled-components';
import {
  type LeaderboardPagedResults,
  LeaderboardTypeParam,
  useLeaderboardQuery,
} from '../../apiHooks/useLeaderboardQuery';
import type { LeaderboardUser, LeaderboardDepartment } from '../../lib/types/LeaderboardUser';
import { useMeQuery } from '../../apiHooks/useMeQuery';
import { useMediaQuery } from '../../lib/hooks/useMediaQuery';
import { NoLeaderboardDataCard } from './components/NoLeaderboardDataCard';
import { useCurrentUserDepartment } from './hooks/useCurrentUserDepartment';

type LeaderboardUIProps = {
  isManager?: boolean;
};

export const LeaderboardUI: FC<LeaderboardUIProps> = ({ isManager }: LeaderboardUIProps) => {
  const [selectedScopeFilter, setSelectedScopeFilter] = useState<LeaderboardTypeParam | undefined>(
    LeaderboardTypeParam.TEAM,
  );
  const { columns, mobileColumns, scopeFilterLabels } = useLeaderboardData(selectedScopeFilter);
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { data: meData } = useMeQuery();
  const currentUserDepartment = useCurrentUserDepartment();
  const {
    data: leaderboardData,
    isFetching,
    refetch,
  } = useLeaderboardQuery({
    type: selectedScopeFilter || LeaderboardTypeParam.TEAM,
    mode: 'top',
    count: 10,
    isManager,
  });

  useEffect(() => {
    refetch();
  }, [selectedScopeFilter, refetch]);

  useEffect(() => {
    if (!isFetching) {
      setIsLoading(false);
    }
  }, [isFetching]);

  const getTableData = (
    data:
      | (LeaderboardUser | LeaderboardDepartment)[]
      | LeaderboardPagedResults<LeaderboardUser | LeaderboardDepartment>
      | undefined,
  ): (LeaderboardUser | LeaderboardDepartment)[] => {
    if (Array.isArray(data)) {
      return data;
    }
    return data?.values || [];
  };

  const tableData = getTableData(leaderboardData);
  const isScopeToggleDisabled = isFetching || isLoading;

  const onScopeFilterChange = (index: number) => {
    if (isScopeToggleDisabled) {
      return;
    }

    const nextScope = scopeFilterLabels[index]?.value;

    if (!nextScope || nextScope === selectedScopeFilter) {
      return;
    }

    setIsLoading(true);
    setSelectedScopeFilter(nextScope);
  };

  // const onDateFilterChange = (index: number) => {
  //   console.log(`Selected date: ${dateFilterLabels[index]?.value}`);
  // };

  return (
    <Card>
      <FiltersBar>
        <LabelledToggle
          labels={scopeFilterLabels}
          disabled={isScopeToggleDisabled}
          onSelect={(selectedIndex: number) => onScopeFilterChange(selectedIndex)}
        />
        {/*<LabelledToggle*/}
        {/*  labels={dateFilterLabels}*/}
        {/*  onSelect={(selectedIndex: number) => onDateFilterChange(selectedIndex)}*/}
        {/*/>*/}
      </FiltersBar>
      <Table
        data={tableData}
        columns={isMobile ? mobileColumns : columns}
        roundedRows={!isMobile}
        hideSeparator={!isMobile}
        hideHeaders={isMobile}
        rowColorSelector={(data: LeaderboardUser | LeaderboardDepartment) => {
          // Highlight current user in team/company leaderboards
          if ('learnerId' in data) {
            return data.learnerId === meData?.learnerId ? theme.colors.prototype.secondary : 'unset';
          }
          // Highlight current user's department in department leaderboard
          if ('department' in data && currentUserDepartment) {
            return data.department === currentUserDepartment ? theme.colors.prototype.secondary : 'unset';
          }
          return 'unset';
        }}
        renderZeroState={() => <NoLeaderboardDataCard />}
        isLoading={isFetching || isLoading}
      />
    </Card>
  );
};

const FiltersBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
